<script setup>
import { computed, getCurrentInstance, watch } from 'vue';
import { useAsyncState } from '@vueuse/core';
import FormControlCategoryTitle from '../../common/form-control/FormControlCategoryTitle.vue';
import Button from "../Button.vue";
import ItemStatusIcons from "../ItemStatusIcons.vue";

const props = defineProps({ field: Object, item: Object });
const { proxy } = getCurrentInstance();
const store = proxy.$store;

const { state: content, execute: fetchContent } = useAsyncState(
    () => store.dispatch('itemData/get', { tableName: 'content', id: props.item.contentId }),
    null
);

const { state: contentType, execute: fetchContentType } = useAsyncState(
    async () => {
        if (!content.value) return null;
        return store.dispatch('itemData/get', { tableName: 'templates_templates', id: content.value.contentType });
    },
    null,
    { immediate: false }
);

watch(() => props.item.contentId, () => fetchContent(), { immediate: true });
watch(content, (val) => { if (val) fetchContentType(); });

const componentId = computed(() => contentType.value?.componentId);
const tableName = computed(() => {
    const targetId = props.item.fieldId === null ? componentId.value : props.field?.categoryComponentId;
    return store.state.components.items.find(o => o.id === targetId)?.tableName;
});

const itemIds = computed({
    get: () => props.item.value,
    set: (val) => { props.item.value = val; }
});

const openSelectListModal = () => {
    store.dispatch('modals/show', {
        componentName: 'SelectListModal',
        obj: {
            listingName: `${tableName.value}.${props.item.fieldId}`,
            selectedIds: itemIds.value || [],
            onSelect: (ids) => { itemIds.value = ids; }
        }
    });
};

const removeItem = (id) => { itemIds.value = itemIds.value.filter(o => o !== parseInt(id)); };
const moveUp = (i) => moveItem(i, -1);
const moveDown = (i) => moveItem(i, 1);
const moveItem = (i, dir) => {
    const target = i + dir;
    const ids = [...itemIds.value];
    if (target < 0 || target >= ids.length) return;
    [ids[i], ids[target]] = [ids[target], ids[i]];
    itemIds.value = ids;
};
</script>

<template>
    <div v-if="tableName" class="w-100">
        <div v-for="(itemId, i) in itemIds" :key="i" class="mb-1 text-bg-light rounded p-1 small d-flex align-items-center">
            <span class="me-2 text-secondary">{{ i + 1 }}.</span>
            <FormControlCategoryTitle :table-name="tableName" :id="parseInt(itemId)" class="flex-fill"/>
            <ItemStatusIcons :id="itemId" :table-name="tableName"/>
            <Button :disabled="i === 0" @click.native="moveUp(i)" class="ms-2 btn-sm border"><i class="bi bi-chevron-up"/></Button>
            <Button :disabled="i === itemIds.length - 1" @click.native="moveDown(i)" class="ms-2 btn-sm border"><i class="bi bi-chevron-down"/></Button>
            <Button @click.native="removeItem(itemId)" class="ms-2 btn-sm border"><i class="bi bi-x"/></Button>
        </div>
        <Button @click.native="openSelectListModal" class="btn-sm border"><i class="bi bi-plus-lg"/></Button>
    </div>
    <small v-else-if="!componentId" class="text-danger form-text ms-2 mb-1 fst-italic small">Error: No collection targeted</small>
</template>